import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { GoogleLogo, ArrowRight, Target, ChartLineUp, Lightning, LockKey } from "@phosphor-icons/react";

const AUTH_ERRORS = {
  not_authorized: {
    title: "Accès refusé",
    message: "Votre compte n'est pas autorisé à accéder à Crédit Flow.",
  },
  account_disabled: {
    title: "Accès désactivé",
    message: "Votre accès à Crédit Flow a été désactivé. Contactez votre administrateur.",
  },
  auth_failed: {
    title: "Connexion échouée",
    message: "Impossible d'établir la session. Réessayez.",
  },
};

export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!loading && user) navigate("/", { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const code = qs.get("error");
    if (code && AUTH_ERRORS[code]) setError(AUTH_ERRORS[code]);
  }, [location.search]);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-5 py-10"
      style={{ background: "linear-gradient(180deg, var(--cf-bg) 0%, var(--cf-primary-soft) 100%)" }}
      data-testid="login-page"
    >
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center text-center mb-10">
          <div className="w-14 h-14 rounded-2xl bg-[color:var(--cf-primary)] flex items-center justify-center shadow-[0_8px_24px_rgba(45,110,255,0.4)] mb-5">
            <div className="w-5 h-5 bg-white rounded-md rotate-45" />
          </div>
          <div className="overline mb-3">CRM Courtier · Pilotage</div>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter text-slate-900">
            Crédit Flow
          </h1>
          <p className="mt-3 text-slate-500 text-base leading-relaxed max-w-sm">
            L'outil d'exécution quotidienne des courtiers. Lisez votre business en 5 secondes.
          </p>
        </div>

        {error && (
          <div
            className="mb-5 rounded-2xl border-2 border-red-200 bg-red-50 px-4 py-3 flex items-start gap-3"
            data-testid="login-error"
          >
            <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center shrink-0">
              <LockKey size={16} weight="bold" className="text-red-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-red-900 text-sm" data-testid="login-error-title">{error.title}</div>
              <div className="text-red-800 text-[13px] mt-0.5 leading-snug">{error.message}</div>
            </div>
          </div>
        )}

        <div className="card-base p-7">
          <div className="mb-5">
            <div className="font-display text-xl font-extrabold text-slate-900">Connexion</div>
            <p className="text-sm text-slate-500 mt-1">Accès sécurisé sur invitation — via votre compte Google.</p>
          </div>

          <button
            onClick={handleGoogle}
            className="group inline-flex items-center justify-between gap-3 bg-[color:var(--cf-primary)] text-white rounded-2xl px-5 py-4 font-bold text-sm w-full hover:bg-[color:var(--cf-primary-hover)] transition-all shadow-[0_8px_24px_rgba(45,110,255,0.35)] active:scale-[0.98]"
            data-testid="login-google-btn"
          >
            <span className="inline-flex items-center gap-3">
              <GoogleLogo size={20} weight="bold" />
              Continuer avec Google
            </span>
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </button>

          <div className="grid grid-cols-3 gap-2 mt-6 pt-6 border-t border-slate-100">
            {[
              { icon: Target, label: "Focus" },
              { icon: ChartLineUp, label: "Cash" },
              { icon: Lightning, label: "Pilotage" },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex flex-col items-center gap-1 text-slate-400">
                <Icon size={18} />
                <span className="text-[11px] font-semibold">{label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center mt-6 text-[11px] text-slate-400 tracking-widest uppercase font-semibold">
          Accès privé — sur invitation
        </div>
      </div>
    </div>
  );
}
